/*
INTELLECTUAL PROPERTY NOTICE
© Orbit Tickets Ltd. All rights reserved.
This file contains Custom / Included Intellectual Property created for and owned by the Customer as part of an application developed by Kaleida within the Platform.
Custom / Included Intellectual Property may be used, reproduced, adapted, and incorporated into any application, system, or product developed by or on behalf of the Customer, whether or not such application forms part of the Platform, subject always to the terms of the agreement and provided that such use does not include or rely upon any Protected Intellectual Property of Kaleida Ltd.
For the avoidance of doubt, the inclusion of any application within the Platform does not restrict the Customer's right to use Custom / Included Intellectual Property outside of the Platform.
*/

// IP-NOTICE: CUSTOM
import { Injectable } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { Sort, SortDirection } from '@angular/material/sort';
import { OrbitTableColumnConfig, OrbitTableConfig } from './orbit-table.models';

/**
 * Component-scoped state and data adapter for the reusable Orbit table.
 *
 * Keeping client sorting, paging and config synchronisation here means every
 * table consumer uses the same behaviour and the renderer remains focused on
 * markup, accessibility and events.
 */
@Injectable()
export class OrbitTableService<T> {
	private config: OrbitTableConfig<T> | null = null;
	private rows: readonly T[] = [];

	pageIndex = 0;
	pageSize = 20;
	sortActive = '';
	sortDirection: SortDirection = '';

	setRows(rows: readonly T[] | null | undefined): void {
		this.rows = rows ?? [];
		this.ensureValidPage();
	}

	setConfig(config: OrbitTableConfig<T>, force = false): void {
		const previous = this.config;
		this.config = config;

		if (force || previous?.sorting?.active !== config.sorting?.active || previous?.sorting?.direction !== config.sorting?.direction) {
			this.sortActive = config.sorting?.active ?? '';
			this.sortDirection = config.sorting?.direction ?? '';
		}

		if (force || previous?.pagination?.pageIndex !== config.pagination?.pageIndex || previous?.pagination?.pageSize !== config.pagination?.pageSize) {
			this.pageIndex = config.pagination?.pageIndex ?? 0;
			this.pageSize = config.pagination?.pageSize ?? 20;
		}

		this.ensureValidPage();
	}

	get displayedRows(): readonly T[] {
		let result = [...this.rows];
		if (this.config?.sorting?.enabled && this.config.sorting.mode !== 'server' && this.sortActive && this.sortDirection) {
			const column = this.config.columns.find(item =>
				(item.sortField ?? item.key) === this.sortActive || item.key === this.sortActive
			);
			if (column) {
				const direction = this.sortDirection === 'asc' ? 1 : -1;
				result.sort((left, right) => this.compare(this.columnValue(column, left), this.columnValue(column, right)) * direction);
			}
		}

		if (this.config?.pagination?.enabled && this.config.pagination.mode !== 'server') {
			const start = this.pageIndex * this.pageSize;
			result = result.slice(start, start + this.pageSize);
		}

		return result;
	}

	get rowCount(): number {
		return this.rows.length;
	}

	columnValue(column: OrbitTableColumnConfig<T>, row: T): unknown {
		if (column.value) return column.value(row);
		const path = String(column.property ?? column.key).split('.');
		return path.reduce<unknown>((value, key) => value == null ? value : (value as Record<string, unknown>)[key], row);
	}

	applySort(sort: Sort): void {
		this.sortActive = sort.active;
		this.sortDirection = sort.direction;
		this.pageIndex = 0;
	}

	applyPage(page: PageEvent): void {
		this.pageIndex = page.pageIndex;
		this.pageSize = page.pageSize;
	}

	private ensureValidPage(): void {
		if (!this.config?.pagination?.enabled || this.config.pagination.mode === 'server') return;
		const lastPage = Math.max(0, Math.ceil(this.rows.length / this.pageSize) - 1);
		this.pageIndex = Math.min(this.pageIndex, lastPage);
	}

	private compare(left: unknown, right: unknown): number {
		if (Object.is(left, right)) return 0;
		if (left == null) return -1;
		if (right == null) return 1;
		if (typeof left === 'number' && typeof right === 'number') return left - right;
		return String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: 'base' });
	}
}
